import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Form, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useProjectSprints } from '@hooks/projects/useProjectSprints';
import { submitProjectToQA } from '@services/projects.service';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { contentToApiMentions } from '@utils/richTextMentions';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { PROJECT_QA_SUBMISSIONS_QUERY_KEY } from './useProjectQASubmissions';
import { getSprintTaskApiId, normalizePhaseLinkUrl } from '@hooks/projects/projectHelpers';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';

function submissionEditorHrefMatchesStored(storedUrl, editorHref) {
    const a = String(storedUrl || '').trim();
    const b = String(editorHref || '').trim();
    if (!a || !b) return false;
    if (a === b) return true;
    try {
        return normalizePhaseLinkUrl(a) === normalizePhaseLinkUrl(b);
    } catch {
        return false;
    }
}

function submissionLinkStillInHtml(html, url) {
    const u = String(url || '').trim();
    if (!u || !html || typeof html !== 'string') return false;
    if (html.includes(u)) return true;
    try {
        const n = normalizePhaseLinkUrl(u);
        if (n && html.includes(n)) return true;
    } catch {
        /* ignore */
    }
    return false;
}

/**
 * Submission state for Sprints tab: drag sprint/task from board into right panel.
 * Submission description (rich HTML in UI; converted to markdown for API) and submit-to-QA API.
 * Only fetches sprints when isSprintsTabActive is true so we don't hit GET /sprints on other tabs (avoids 403 when user lacks sprints permission).
 *
 * @param {string} projectId
 * @param {{ isSprintsTabActive?: boolean }} options - When false, sprints query is disabled.
 */
export function useProjectSprintSubmission(projectId, options = {}) {
    const queryClient = useQueryClient();
    const isSprintsTabActive = options.isSprintsTabActive !== false;
    /** When parent passes `sprints` (e.g. from GET /projects/:id), do not call GET /projects/:id/sprints. */
    const sprintsFromParent = Object.prototype.hasOwnProperty.call(options, 'sprints') ? options.sprints : undefined;
    const { sprints: fetchedSprints, isLoading, refetch } = useProjectSprints(projectId, {
        enabled: isSprintsTabActive && sprintsFromParent === undefined,
    });
    const sprints = sprintsFromParent !== undefined ? (Array.isArray(sprintsFromParent) ? sprintsFromParent : []) : fetchedSprints;

    /** Derive QA / PMO module flags from project modules (set via Project Settings toggles). */
    const isQAEnabled = options.project?.modules?.isQA?.enabled === true;
    const isPMOEnabled = options.project?.modules?.isPMO?.enabled === true;

    /** submittedTo: default to 'qa' when QA is on, 'pmo' when only PMO is on, null for memo-only. */
    const defaultSubmittedTo = isQAEnabled ? 'qa' : isPMOEnabled ? 'pmo' : null;
    const [submittedTo, setSubmittedTo] = useState(defaultSubmittedTo);

    /** Keep submittedTo in sync when project capabilities load/change. */
    const prevDefaultRef = useRef(defaultSubmittedTo);
    useEffect(() => {
        if (prevDefaultRef.current !== defaultSubmittedTo) {
            prevDefaultRef.current = defaultSubmittedTo;
            setSubmittedTo(defaultSubmittedTo);
        }
    }, [defaultSubmittedTo]);
    const [submissionPanelOpen, setSubmissionPanelOpen] = useState(false);
    const [submittedItems, setSubmittedItems] = useState([]);
    /** Rich-text HTML from RichTextEditor (converted to markdown on submit). */
    const [submissionNotes, setSubmissionNotes] = useState('');
    const [submissionPendingFiles, setSubmissionPendingFiles] = useState([]);
    const [submissionPendingLinks, setSubmissionPendingLinks] = useState([]);
    const [submissionLinkModalOpen, setSubmissionLinkModalOpen] = useState(false);
    const [submissionLinkForm] = Form.useForm();
    const submissionLinkCallbackRef = useRef(null);
    const [submissionSession, setSubmissionSession] = useState(0);
    const wasPanelOpenRef = useRef(false);

    useEffect(() => {
        if (submissionPanelOpen && !wasPanelOpenRef.current) {
            setSubmissionSession((s) => s + 1);
        }
        wasPanelOpenRef.current = submissionPanelOpen;
    }, [submissionPanelOpen]);

    /** When note HTML changes, drop pending link chips that no longer appear in the editor (same idea as sprint/task modals). */
    useEffect(() => {
        const html = submissionNotes;
        if (!html || typeof html !== 'string') {
            setSubmissionPendingLinks([]);
            return;
        }
        setSubmissionPendingLinks((prev) =>
            prev.filter((x) => !x?.url || submissionLinkStillInHtml(html, x.url)),
        );
    }, [submissionNotes]);

    const openSubmissionPanel = useCallback(() => setSubmissionPanelOpen(true), []);

    const addToSubmission = useCallback(
        (item) => {
            setSubmittedItems((prev) => {
                const keyOf = (i) => `${i.type}:${String(i.id)}`;
                const itemKey = keyOf(item);
                if (item.type !== 'sprint' && prev.some((i) => keyOf(i) === itemKey)) return prev;
                if (item.type === 'sprint') {
                    const sprint = sprints.find((s) => String(s.id) === String(item.id));
                    const allTasks = Array.isArray(sprint?.tasks) ? sprint.tasks : [];
                    const tasks = allTasks.filter((t) => String(t?.status).toLowerCase() === 'completed');
                    const taskItems = tasks.map((t) => ({
                        type: 'task',
                        id: getSprintTaskApiId(t) ?? t.id ?? t.taskId,
                        name: t.title ?? t.name ?? 'Task',
                        sprintId: item.id,
                    }));
                    const existingKeys = new Set(prev.map((i) => keyOf(i)));
                    const toAdd = [];
                    if (!existingKeys.has(keyOf(item))) toAdd.push(item);
                    taskItems.forEach((t) => {
                        if (!existingKeys.has(keyOf(t))) toAdd.push(t);
                    });
                    return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
                }
                return [...prev, item];
            });
        },
        [sprints],
    );

    /** Add many tasks in one update (column dropdown “Submitted” bulk). */
    const addTasksToSubmissionBatch = useCallback((taskEntries) => {
        if (!Array.isArray(taskEntries) || taskEntries.length === 0) return;
        setSubmittedItems((prev) => {
            const keyOf = (i) => `${i.type}:${String(i.id)}`;
            const existingKeys = new Set(prev.map((i) => keyOf(i)));
            const toAdd = [];
            for (const item of taskEntries) {
                if (item?.type !== 'task' || item.id == null) continue;
                if (existingKeys.has(keyOf(item))) continue;
                toAdd.push(item);
                existingKeys.add(keyOf(item));
            }
            return toAdd.length ? [...prev, ...toAdd] : prev;
        });
    }, []);

    const removeFromSubmission = useCallback((type, id) => {
        setSubmittedItems((prev) => {
            const matchId = (a, b) => (a == null && b == null ? true : String(a) === String(b));
            if (type === 'sprint') {
                return prev.filter(
                    (i) =>
                        (i.type === 'sprint' && !matchId(i.id, id)) || (i.type === 'task' && !matchId(i.sprintId, id)),
                );
            }
            return prev.filter((i) => i.type !== type || !matchId(i.id, id));
        });
    }, []);

    const clearSubmission = useCallback(() => {
        setSubmittedItems([]);
        setSubmissionNotes('');
        setSubmissionPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        /* ignore */
                    }
                }
            });
            return [];
        });
        setSubmissionPendingLinks([]);
    }, []);

    const closeSubmissionPanel = useCallback(() => {
        // Cancel should behave like a full reset so reopening starts clean.
        setSubmissionLinkModalOpen(false);
        submissionLinkCallbackRef.current = null;
        try {
            submissionLinkForm.resetFields();
        } catch {
            /* ignore */
        }
        setSubmissionPanelOpen(false);
        clearSubmission();
    }, [clearSubmission, submissionLinkForm]);

    const removeSubmissionPendingFile = useCallback((id) => {
        setSubmissionPendingFiles((prev) => {
            const index = prev.findIndex((p) => p.id === id);
            const removedAt = index >= 0 ? prev[index] : null;
            if (removedAt?.preview) {
                try {
                    URL.revokeObjectURL(removedAt.preview);
                } catch {
                    /* ignore */
                }
            }
            if (index < 0) return prev.filter((p) => p.id !== id);
            setSubmissionNotes((content) => {
                let c = content || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                c = c.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    c = c.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                return c;
            });
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const removeSubmissionPendingLink = useCallback((id) => {
        setSubmissionPendingLinks((prev) => {
            const removed = prev.find((p) => p.id === id);
            const next = prev.filter((p) => p.id !== id);
            if (removed?.url) {
                const href = String(removed.url);
                const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                setSubmissionNotes((content) => {
                    const c = String(content || '');
                    const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                    return c.replace(re, '');
                });
            }
            return next;
        });
    }, []);

    const handleSubmissionFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `sub-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setSubmissionPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const handleSubmissionLinkRequest = useCallback(
        (insertLink) => {
            submissionLinkCallbackRef.current = insertLink;
            submissionLinkForm.resetFields();
            setSubmissionLinkModalOpen(true);
        },
        [submissionLinkForm],
    );

    const closeSubmissionLinkModal = useCallback(() => {
        setSubmissionLinkModalOpen(false);
        submissionLinkCallbackRef.current = null;
    }, []);

    const handleSubmissionLinkModalSubmit = useCallback(() => {
        submissionLinkForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                submissionLinkForm.setFields([{ name: 'url', errors: [check.message || 'Enter a valid URL'] }]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = submissionLinkCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setSubmissionPendingLinks((prev) => [
                ...prev,
                { id: `link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setSubmissionLinkModalOpen(false);
            submissionLinkCallbackRef.current = null;
        });
    }, [submissionLinkForm]);

    /** RichTextEditor: user removed an external link from the note — sync pending link chips. */
    const handleSubmissionEditorLinkRemoved = useCallback((href) => {
        if (!href) return;
        setSubmissionPendingLinks((prev) => prev.filter((p) => !submissionEditorHrefMatchesStored(p.url, href)));
    }, []);

    /** Build payload for submit-to-QA API. milestoneId from first submitted sprint (or first task's sprint). */
    const buildSubmissionPayload = useCallback(
        (opts = {}) => {
            const sprintIds = submittedItems.filter((i) => i.type === 'sprint').map((i) => String(i.id));
            const taskIds = submittedItems.filter((i) => i.type === 'task').map((i) => String(i.id));
            let milestoneId = null;
            const firstSprintId = sprintIds[0] ?? submittedItems.find((i) => i.type === 'task')?.sprintId;
            if (firstSprintId != null) {
                const sprint = sprints.find((s) => String(s.id) === String(firstSprintId));
                milestoneId = sprint?.milestoneId ?? sprint?.milestone?.id ?? null;
            }
            const notesRaw = (opts.notes ?? submissionNotes).trim();
            let notesMarkdown = '';
            if (notesRaw) {
                try {
                    const forApi = contentToApiMentions(notesRaw);
                    notesMarkdown = htmlToMarkdown(forApi).trim();
                } catch {
                    notesMarkdown = String(notesRaw).trim();
                }
            }
            return {
                milestoneId: milestoneId ?? undefined,
                ...(submittedTo != null && { submittedTo }),
                sprintIds,
                taskIds,
                notes: notesMarkdown ? { markdown: notesMarkdown } : undefined,
                attachmentUrls: Array.isArray(opts.attachmentUrls) ? opts.attachmentUrls : undefined,
                files: opts.files,
            };
        },
        [submittedItems, submissionNotes, sprints, submittedTo],
    );

    const submitToQAMutation = useMutation({
        mutationFn: (opts) => submitProjectToQA(projectId, buildSubmissionPayload(opts)),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            // Ensure QA tab shows new task cards without a full refresh.
            // - remove: drops any previous list shape
            // - refetch: if QA tab is mounted (tabs UI), it pulls immediately
            const qaKey = PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId);
            queryClient.removeQueries({ queryKey: qaKey });
            queryClient.refetchQueries({ queryKey: qaKey, type: 'all' });
        },
    });

    const submitToQA = useCallback(
        (callbacks = {}) => {
            if (submittedItems.length === 0) return;
            const { notes, onSuccess, onError } = callbacks;
            const linkAttachments = submissionPendingLinks.map(({ url, title }) => ({
                type: 'link',
                url,
                title: title || url,
            }));
            const files = submissionPendingFiles.map((p) => p.file);
            submitToQAMutation.mutate(
                {
                    notes: notes ?? submissionNotes,
                    attachmentUrls: linkAttachments.length > 0 ? linkAttachments : undefined,
                    files: files.length > 0 ? files : undefined,
                },
                {
                    onSuccess: () => {
                        clearSubmission();
                        closeSubmissionPanel();
                        submitToQAMutation.reset?.();
                        onSuccess?.();
                    },
                    onError,
                },
            );
        },
        [
            submittedItems.length,
            submissionNotes,
            submissionPendingFiles,
            submissionPendingLinks,
            submitToQAMutation,
            clearSubmission,
            closeSubmissionPanel,
        ],
    );

    const submittedSprintIds = useMemo(
        () => new Set(submittedItems.filter((i) => i.type === 'sprint').map((i) => String(i.id))),
        [submittedItems],
    );
    /** String IDs so lookups work regardless of number vs string from API. */
    const submittedTaskIds = useMemo(
        () => new Set(submittedItems.filter((i) => i.type === 'task').map((i) => String(i.id))),
        [submittedItems],
    );

    /** Column disabled only when every task in the sprint is submitted. Remove one task → column and that task become enabled. */
    const isSprintColumnDisabled = useCallback(
        (sprint) => {
            const tasks = Array.isArray(sprint.tasks) ? sprint.tasks : [];
            if (tasks.length === 0) return false;
            return tasks.every((t) =>
                submittedTaskIds.has(String(getSprintTaskApiId(t) ?? t.id ?? t.taskId)),
            );
        },
        [submittedTaskIds],
    );

    /** Task is submitted only if it is in submittedTaskIds (so removing a task from panel re-enables it on the board). */
    const isTaskSubmitted = useCallback(
        (task) => submittedTaskIds.has(String(getSprintTaskApiId(task) ?? task?.id ?? task?.taskId)),
        [submittedTaskIds],
    );

    /** Sprint can be dragged only when it has tasks and all tasks are completed. Sprints submitted to QA or PM are disabled. */
    const canDragSprint = useCallback((sprint) => {
        const tasks = Array.isArray(sprint.tasks) ? sprint.tasks : [];
        if (tasks.length === 0) return false;
        const status = String(sprint?.status ?? '').toLowerCase();
        if (status === 'submitted_to_qa' || status === 'submitted_to_pm') return false;
        const allCompleted = tasks.every((t) => String(t?.status).toLowerCase() === 'completed');
        return allCompleted;
    }, []);

    const submissionGroupedBySprint = useMemo(() => {
        const bySprint = new Map();
        submittedItems.forEach((i) => {
            const sid = i.type === 'sprint' ? i.id : i.sprintId;
            if (sid == null) return;
            if (!bySprint.has(sid)) {
                const sprint = sprints.find((s) => s.id === sid);
                bySprint.set(sid, {
                    sprintId: sid,
                    sprintName: i.type === 'sprint' ? i.name : (sprint?.name ?? 'Sprint'),
                    isSprintSubmitted: false,
                    tasks: [],
                });
            }
            const row = bySprint.get(sid);
            if (i.type === 'sprint') {
                row.isSprintSubmitted = true;
                row.sprintName = i.name;
            } else {
                row.tasks.push({ id: i.id, name: i.name });
            }
        });
        return Array.from(bySprint.values());
    }, [submittedItems, sprints]);

    /** Same as submissionGroupedBySprint but with full sprint and task objects for same UI as board. */
    const submissionGroupedBySprintEnriched = useMemo(() => {
        const findTaskOnSprint = (sprint, queuedId) => {
            if (!sprint || queuedId == null) return null;
            const list = Array.isArray(sprint.tasks) ? sprint.tasks : [];
            const q = String(queuedId);
            return (
                list.find((x) => {
                    const api = getSprintTaskApiId(x);
                    if (api != null && String(api) === q) return true;
                    if (x?.id != null && String(x.id) === q) return true;
                    if (x?.taskId != null && String(x.taskId) === q) return true;
                    return false;
                }) ?? null
            );
        };
        return submissionGroupedBySprint.map((group) => {
            const fullSprint = sprints.find((s) => String(s.id) === String(group.sprintId)) ?? null;
            const fullTasks = (group.tasks || []).map((t) => {
                const taskFromSprint = findTaskOnSprint(fullSprint, t.id);
                return taskFromSprint ?? t;
            });
            return { ...group, fullSprint, fullTasks };
        });
    }, [submissionGroupedBySprint, sprints]);

    return {
        sprints,
        isLoading,
        refetch,
        submissionPanelOpen,
        openSubmissionPanel,
        closeSubmissionPanel,
        submissionSession,
        submittedItems,
        submissionNotes,
        setSubmissionNotes,
        submissionPendingFiles,
        submissionPendingLinks,
        submissionLinkModalOpen,
        submissionLinkForm,
        handleSubmissionFilesSelected,
        handleSubmissionLinkRequest,
        handleSubmissionLinkModalSubmit,
        closeSubmissionLinkModal,
        removeSubmissionPendingFile,
        removeSubmissionPendingLink,
        handleSubmissionEditorLinkRemoved,
        addToSubmission,
        addTasksToSubmissionBatch,
        removeFromSubmission,
        clearSubmission,
        buildSubmissionPayload,
        submitToQA,
        submitToQAPending: submitToQAMutation.isPending,
        isSprintColumnDisabled,
        isTaskSubmitted,
        canDragSprint,
        submissionGroupedBySprint,
        submissionGroupedBySprintEnriched,
        /** Submission routing flags */
        isQAEnabled,
        isPMOEnabled,
        submittedTo,
        setSubmittedTo,
    };
}
